import { COUNTRY_CODE_KEY } from "@/constants/CookieValues";
import { parseCountryCode } from "@/libs/locale/cookie";
import { LanCode } from "@/store/reducers/setting";
import { getCookie } from "cookies-next";
import type { IncomingMessage, ServerResponse } from "http";

export type LanguageCode = "en" | "es" | "fr" | "de" | "ko" | "ja" | "zh";
export const LanguageCodeToLanCode: Record<LanguageCode, LanCode> = {
  en: LanCode.English,
  ko: LanCode.Korean, // 한국어는 kr
  zh: LanCode.Chinese, // 중국어는 cn
  ja: LanCode.Japanese, // 일본어는 jp
  de: LanCode.German,
  fr: LanCode.French,
  es: LanCode.Spanish,
};

// candidateCountryCode 가 locale 에 있는 언어를 지원하면 해당 국가코드를 반환
// 아니면 locale 에서 파싱된 국가코드를 반환
export const getResolvedCountryCode = (
  locale: string,
  candidateCountryCode?: string,
) => {
  const parsedLocale = parseLocale(locale);

  if (!!candidateCountryCode) {
    const supportedLanguageCodes =
      getSupportedLanguageCodes(candidateCountryCode);
    if (supportedLanguageCodes.includes(parsedLocale.languageCode)) {
      return candidateCountryCode;
    }
  }

  return parsedLocale.countryCode;
};

/**
 * 서버사이드에서 countryCode 를 반환.
 *
 * 우선순위:
 *   1. `bh_ccode` 쿠키 (ISO 3166-1 alpha-2 검증 통과)
 *   2. Vercel IP 헤더 (`x-vercel-ip-country`)
 *   3. locale 의 default country (`parseLocale`)
 *
 * 검증/정규화는 `@/libs/locale/cookie.parseCountryCode` 단일 헬퍼 사용.
 */
export function getServerSideCountryCode(
  locale: string,
  req: IncomingMessage,
  res: ServerResponse,
): string {
  const cookieCountry = parseCountryCode(
    getCookie(COUNTRY_CODE_KEY, { req, res }),
  );
  const ipCountry = parseCountryCode(req.headers["x-vercel-ip-country"]);
  return getResolvedCountryCode(locale, cookieCountry ?? ipCountry);
}

export const LOCALES = ["en", "ko", "ja", "zh", "es", "fr", "de"] as const;

export type Locale = (typeof LOCALES)[number];

/**
 * 국가의 기본(default) UI 언어.
 *
 * - 미들웨어 첫 방문 redirect(`src/middleware.ts`)와 geo API(`/api/geo`)가 사용.
 * - 단일 모국어가 명확한 국가는 그 언어를, 다국어 국가(스위스/벨기에 등)나 지원하지
 *   않는 언어권(브라질=포르투갈어, 대만=번체 등)은 영어를 반환한다.
 *
 * 기본 언어는 `COUNTRY_SUPPORTED_LANGUAGE_CODE_MAP` 의 **첫 요소**를 그대로 사용한다.
 * (예전엔 이 함수와 supported 맵이 따로 하드코딩돼 drift 위험이 있었음 —
 *  docs/locale-middleware-policy.md §4.6. 둘을 단일 소스로 통합.)
 *
 * ⚠️ 새 국가 추가/기본 언어 변경은 `COUNTRY_SUPPORTED_LANGUAGE_CODE_MAP` 한 곳만 수정.
 */
export function getDefaultLanguageCode(countryCode: string): LanguageCode {
  return getSupportedLanguageCodes(countryCode)[0] ?? "en";
}

export function getDefaultCountryCode(languageCode: string): string {
  const languageCountryMap: Record<LanguageCode, string> = {
    ko: "KR",
    ja: "JP",
    zh: "CN",
    es: "ES",
    fr: "FR",
    de: "DE",
    en: "US",
  };

  return languageCountryMap[languageCode.toLowerCase()] || "US";
}

// 예전 LanCode 를 지원하기 위한 변환 함수
export function languageCodeToLanCode(languageCode: LanguageCode): LanCode {
  return LanguageCodeToLanCode[languageCode] || LanCode.English;
}

export function getLocale(
  countryCode: string,
  languageCode: LanguageCode,
): Locale {
  const locale = `${languageCode}-${countryCode.toUpperCase()}` as const;

  if (LOCALES.includes(locale as Locale)) {
    return locale as Locale;
  }

  return languageCode as Locale;
}

export function parseLocale(locale: string): {
  languageCode: LanguageCode;
  countryCode: string;
} {
  let [languageCode, countryCode] = locale.split("-") as [LanguageCode, string];

  if (!countryCode) {
    countryCode = getDefaultCountryCode(languageCode);
  }

  return { languageCode, countryCode };
}

export const LANGUAGE_NAME_MAP: Record<LanguageCode, string> = {
  en: "English",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  ko: "한국어",
  ja: "日本語",
  zh: "简体中文",
};

export const getLanguageName = (languageCode: LanguageCode) => {
  return LANGUAGE_NAME_MAP[languageCode] || LANGUAGE_NAME_MAP["en"];
};

/**
 * 국가 코드를 **현재 UI 언어로 된 국가명**으로 바꾼다. 푸터 country-select 표기용 (#5350).
 *
 * 국가명 테이블을 들고 오지 않는다 — `data/shippingCountries.json` 은 25KB 이고 배송
 * 가능국 93개만 있어서, 전 페이지에 실리는 푸터가 그걸 import 하면 손해가 크다.
 * `Intl` 은 브라우저·Node(full ICU) 내장이라 payload 가 0 이다.
 *
 * ⚠️ **폴백 로케일 `"en"` 을 반드시 붙인다.** 빼면 데이터 없는 태그가 왔을 때 Intl 이
 * **시스템 기본 로케일**로 떨어져 머신마다 다르게 렌더된다(docs/pitfalls.md, `Intl` 항목).
 * 실측: `["kr","en"]` → `en` 으로 결정(`LANG=en_US` 를 강제해도 동일), `["kr"]` 단독이면
 * 그 머신의 기본 로케일을 탄다.
 *
 * ⚠️ 인자는 **ISO 언어 코드**(`ko`/`ja`/`zh`)여야 한다. 번역 디렉터리 이름인 LanCode
 * (`kr`/`jp`/`cn`)는 BCP-47 이 아니라 데이터가 없다 — `useLocale().languageCode` 는 ISO 다.
 *
 * 가드 셋 다 실측으로 필요했다:
 *   - `""`(SSR 초기값) · `"zz9"` 류 → `Intl` 이 **RangeError 를 던진다**
 *   - `"XX"` 처럼 미등록 코드 → `fallback:"none"` 이라 `undefined`
 *   - `"ZZ"` → CLDR 의 **유효한** "unknown region" 이라 `"알려지지 않은 지역"` 이 나온다.
 *     `bh_ccode` 에 남을 수 있는 값이므로(docs/locale-middleware-policy.md) 따로 걸러낸다
 */
export const getCountryDisplayName = (
  countryCode: string,
  languageCode: string,
): string => {
  const code = (countryCode ?? "").toUpperCase();
  if (!/^[A-Z]{2}$/.test(code) || code === "ZZ") {
    return countryCode ?? "";
  }

  try {
    const displayNames = new Intl.DisplayNames([languageCode, "en"], {
      type: "region",
      fallback: "none",
    });
    return displayNames.of(code) ?? code;
  } catch {
    return code;
  }
};

/**
 * 국가(ISO 3166-1 alpha-2)별 지원 언어 목록.
 *
 * ⚠️ 배열의 **첫 요소 = 그 국가의 기본(default) 언어** (`getDefaultLanguageCode` 가 사용).
 *    이후 요소는 셀렉터에서 선택 가능한 추가 언어(보통 영어 fallback).
 *
 * 매핑 원칙:
 * - 그 나라의 **명확한 단일 모국어**가 지원 7개 언어(en/ko/ja/zh/es/fr/de) 중 하나면
 *   그 언어를 기본(첫 요소)으로 둔다.
 * - **공식 언어가 여럿인 다국어 국가**(스위스/벨기에/룩셈부르크/싱가포르 등)는 단일
 *   모국어가 불명확하므로 **기본은 영어**(첫 요소)로 두되, 지원하는 공식 언어들을
 *   뒤에 나열해 셀렉터에서 선택 가능하게 한다 (캐나다 `["en","fr"]` 와 동일 패턴).
 * - 그 외(미지원 언어권)는 영어.
 *
 * 여기 없는 국가는 `getSupportedLanguageCodes` 가 ROW(`["en"]`)로 처리 → 기본 영어.
 */
/** ⚠️ 읽기 전용으로 export — 부트 스크립트(`locale-ready.ts`)가 앱과 같은 국가 판정을 하려고 싣는다 */
export const COUNTRY_SUPPORTED_LANGUAGE_CODE_MAP: Record<
  string,
  LanguageCode[]
> = {
  // 영어권 / 다국어 (기본 영어, 보조 언어 보유)
  US: ["en", "es"],
  CA: ["en", "fr"], // 퀘벡 프랑스어는 국가코드로 구분 불가 → 기본 영어
  // 공식 언어가 여럿인 다국어 국가: 기본 영어 + 지원하는 공식 언어를 선택지로 노출.
  // (단일 모국어 강제 안 함. 사용자가 공식 언어 선택 시 커머스 국가는 자국 유지.)
  CH: ["en", "de", "fr"], // 스위스 (공식 de/fr/it/rm 중 지원: de·fr)
  BE: ["en", "fr", "de"], // 벨기에 (공식 nl/fr/de 중 지원: fr·de — nl 미지원)
  LU: ["en", "fr", "de"], // 룩셈부르크 (공식 lb/fr/de 중 지원: fr·de)
  SG: ["en", "zh"], // 싱가포르 (공용 en/zh/ms/ta 중 지원: en·zh)
  // 동아시아
  KR: ["ko"],
  JP: ["ja"],
  CN: ["zh"], // 간체(简体). 번체권 TW/HK/MO 는 미지원 → ROW(영어)
  // 독일어권
  DE: ["de"],
  AT: ["de", "en"], // 오스트리아
  LI: ["de", "en"], // 리히텐슈타인
  // 프랑스어권 (프랑스 본토 + 모나코 + 프랑스 해외영토)
  FR: ["fr"],
  MC: ["fr", "en"], // 모나코
  GF: ["fr", "en"], // 프랑스령 기아나
  GP: ["fr", "en"], // 과들루프
  MQ: ["fr", "en"], // 마르티니크
  RE: ["fr", "en"], // 레위니옹
  // 스페인어권 (스페인 + 중남미 전역).
  // 브라질(BR)은 포르투갈어권으로 미지원 → 의도적으로 제외(ROW=영어).
  ES: ["es"],
  MX: ["es", "en"], // 멕시코
  GT: ["es", "en"], // 과테말라
  HN: ["es", "en"], // 온두라스
  SV: ["es", "en"], // 엘살바도르
  NI: ["es", "en"], // 니카라과
  CR: ["es", "en"], // 코스타리카
  PA: ["es", "en"], // 파나마
  CU: ["es", "en"], // 쿠바
  DO: ["es", "en"], // 도미니카공화국
  PR: ["es", "en"], // 푸에르토리코
  CO: ["es", "en"], // 콜롬비아
  VE: ["es", "en"], // 베네수엘라
  EC: ["es", "en"], // 에콰도르
  PE: ["es", "en"], // 페루
  BO: ["es", "en"], // 볼리비아
  PY: ["es", "en"], // 파라과이
  CL: ["es", "en"], // 칠레
  AR: ["es", "en"], // 아르헨티나
  UY: ["es", "en"], // 우루과이
  GQ: ["es", "en"], // 적도기니
};

export const getSupportedLanguageCodes = (
  countryCode: string,
): LanguageCode[] => {
  const REST_OF_WORLD_LANGUAGE_CODES: LanguageCode[] = ["en"];
  return (
    COUNTRY_SUPPORTED_LANGUAGE_CODE_MAP[countryCode.toUpperCase()] ||
    REST_OF_WORLD_LANGUAGE_CODES
  );
};

export const LANGUAGECODE_PRIORITY: LanguageCode[] = [
  "en",
  "es",
  "fr",
  "de",
  "ko",
  "ja",
  "zh",
];
